/**
 * Adjustment-source panel for TransactionDetailModal.
 *
 * Shows the user-authored description and amount, both legs labelled
 * Dr/Cr with party names (implicit self leg labelled "(self)"), and
 * the create/void timestamps.
 */
import type { EntryDetail, LedgerEntry, LedgerSource } from '../../api';
import { Button } from '../../ui/primitives';
import { kinrToInrDisplay } from '../../money/inr';

interface Props {
  detail: EntryDetail;
  source: Extract<LedgerSource, { kind: 'adjustment' }>;
  currentPartyId: number;
  onOpenOtherParty: (partyId: number, entryId: number) => void;
}

export function AdjustmentPanel({
  detail,
  source,
  currentPartyId,
  onOpenOtherParty,
}: Props) {
  const { adjustment, bothLegs } = source;
  return (
    <div className="space-y-4">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-small">
        <dt className="text-ink-muted">Description</dt>
        <dd className="text-right text-ink">{adjustment.description}</dd>
        <dt className="text-ink-muted">Amount</dt>
        <dd className="text-right font-mono text-ink">
          {adjustment.amount} K-INR
          <span className="ml-2 text-caption text-ink-muted">
            ({kinrToInrDisplay(adjustment.amount)})
          </span>
        </dd>
        {adjustment.note && (
          <>
            <dt className="text-ink-muted">Note</dt>
            <dd className="text-right text-ink">{adjustment.note}</dd>
          </>
        )}
        {adjustment.voidedAt && (
          <>
            <dt className="text-ink-muted">Voided</dt>
            <dd className="text-right text-warn">
              {adjustment.voidedAt.slice(0, 10)} — {adjustment.voidedReason}
            </dd>
          </>
        )}
        <dt className="text-ink-muted">Posted</dt>
        <dd className="text-right text-caption text-ink-muted">{adjustment.createdAt}</dd>
      </dl>

      <div>
        <h3 className="mb-2 text-small font-semibold text-ink">Both legs</h3>
        <ul className="space-y-2.5">
          {bothLegs.map((leg) => (
            <LegRow
              key={leg.id}
              leg={leg}
              party={detail.parties[String(leg.partyId)]}
              isCurrent={leg.partyId === currentPartyId}
              onOpen={() => onOpenOtherParty(leg.partyId, leg.id)}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function LegRow({
  leg,
  party,
  isCurrent,
  onOpen,
}: {
  leg: LedgerEntry;
  party: { displayName: string; role: string } | undefined;
  isCurrent: boolean;
  onOpen: () => void;
}) {
  const hasDr = leg.dr !== '0';
  const hasCr = leg.cr !== '0';
  const displayName = party
    ? `${party.displayName}${party.role === 'self' ? ' (self)' : ''}`
    : '—';
  return (
    <li
      className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-small ${
        isCurrent ? 'border-sec-ledgers bg-sec-ledgers-tint' : 'border-border bg-surface'
      }`}
    >
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="truncate font-medium text-ink">{displayName}</span>
        {isCurrent && (
          <span className="shrink-0 text-caption text-ink-muted">(this ledger)</span>
        )}
      </div>
      <div className="flex shrink-0 items-baseline gap-3">
        {hasDr && (
          <span className="font-mono">
            <span className="text-ink-muted">Dr </span>
            <span className="text-ink">{leg.dr}</span>
          </span>
        )}
        {hasCr && (
          <span className="font-mono">
            <span className="text-warn">Cr </span>
            <span className="text-warn">{leg.cr}</span>
          </span>
        )}
        {!isCurrent && (
          <Button size="sm" variant="ghost" onClick={onOpen}>
            Open →
          </Button>
        )}
      </div>
    </li>
  );
}
